<template>
  <div class="custom-input">
    <v-menu
      ref="datePickerMenu"
      v-model="datePickerOpen"
      transition="scale-transition"
      offset-y
      min-width="auto"
      :close-on-content-click="false"
      :return-value.sync="date"
      :disabled="disabled || readonly"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          v-if="isManual"
          type="text"
          :label="$t(label)"
          :placeholder="$t(placeholder)"
          outlined
          color="primary accent-4"
          :dense="dense"
          :disabled="disabled"
          :hide-details="hideDetails"
          :class="inputClass"
          autocomplete="new-password"
          persistent-placeholder
          :rules="rules"
          :clearable="!disabledClear"
          @click:clear="_updateFieldValueFromAction(true)"
          :value="fieldValue"
          @input="_updateFieldInputValue($event)"
          @keypress="formatInput($event)"
        >
          <template v-slot:append>
            <v-icon v-bind="attrs" v-on="on" :color="disabled ? 'colorLightGrey' : 'colorBlack'">{{ icon.mdiCalendarMinus }}</v-icon>
          </template>
        </v-text-field>
        <v-text-field
          v-else
          type="text"
          :label="$t(label)"
          :placeholder="$t(placeholder)"
          outlined
          color="primary accent-4"
          :dense="dense"
          :disabled="disabled"
          :hide-details="hideDetails"
          :class="inputClass"
          autocomplete="new-password"
          persistent-placeholder
          :rules="rules"
          :clearable="!disabledClear"
          @click:clear="_updateFieldValueFromAction(true)"
          :append-icon="icon.mdiCalendarMinus"
          v-bind="attrs"
          v-on="on"
          :value="dateFormat()"
          readonly
        />
      </template>
      <v-date-picker
        no-title
        scrollable
        v-model="date"
        :first-day-of-week="0"
        :day-format="$Formatter.dayFormat"
        :locale="locale"
        @input="_updateFieldValue($event)"
        :type="isYearMonthOnly ? 'month' : 'date'"
        :range="isDateRange"
        :readonly="isReadOnly"
        :min="dateMin"
        :max="dateMax"
        :show-current="dateMax"
      >
        <template v-if="!disabledActionButtons">
          <v-spacer></v-spacer>
          <div class="d-flex justify-space-between" style="width: 100%">
            <v-btn min-width="max-content" depressed small text color="error" @click="datePickerOpen = false">
              <v-icon class="mr-1" small> {{ icon.mdiClose }} </v-icon>{{ $t('cancel') }}
            </v-btn>
            <v-btn min-width="max-content" depressed small text color="primary" @click="_updateFieldValueFromAction(false)">
              <v-icon class="mr-1" small> {{ icon.mdiCheck }} </v-icon>{{ $t('confirm') }}
            </v-btn>
          </div>
        </template>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import { mdiCalendarMinus, mdiClose, mdiCheck } from '@mdi/js'

export default {
  name: 'FormDatePicker',
  props: {
    label: {
      type: String,
      required: false,
      default: '',
    },
    fieldValue: {
      // YYYY-mm-dd | YYYY-mm
      type: String | Array,
      required: true,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: false,
    },
    isYearMonthOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDateRange: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabledActionButtons: {
      type: Boolean,
      required: false,
      default: false,
    },
    disablePast: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableFuture: {
      type: Boolean,
      required: false,
      default: false,
    },
    customInputClass: {
      type: String,
      required: false,
      default: '',
    },
    minDate: {
      type: String, // YYYY-MM-DD
      default: '',
    },
    maxDate: {
      type: String, // YYYY-MM-DD
      default: '',
    },
    customRules: {
      type: Array,
      required: false,
      default: () => [],
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabledClear: {
      type: Boolean,
      required: false,
      default: false,
    },
    manualInput: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    rules() {
      return [
        v => this.required ? this.$validate.required(v) || this.$t('form.required') : true,
        v => this.required && this.isDateRange ? (this.$validate.DataValid(this.date[0]) && this.$validate.DataValid(this.date[1])) || this.$t('form.dateRangeInv') : true,
        v => this.isManual && v && v.length ? this.$validate.regexDateStr(v) || this.$t('form.dateInv') : true,
        v => !this.isDateRange && !this.isYearMonthOnly && v && v.length && this.dateMin ? (this.$validate.isDateGreater(new Date(v), new Date(this.dateMin)) || this.$validate.isDateEqual(new Date(v), new Date(this.dateMin)) || this.$t('form.dateMinInv')) : true,
        v => !this.isDateRange && !this.isYearMonthOnly && v && v.length && this.dateMax ? (this.$validate.isDateLess(new Date(v), new Date(this.dateMax)) || this.$validate.isDateEqual(new Date(v), new Date(this.dateMax)) || this.$t('form.dateMinInv')) : true,
        ...this.customRules,
      ]
    },
    dateMin() {
      if (this.$validate.DataValid(this.minDate)) {
        return this.minDate
      } else if (this.disablePast) {
        return this.$Formatter.formatDate(new Date())
      }

      return undefined
    },
    dateMax() {
      if (this.$validate.DataValid(this.maxDate)) {
        return this.maxDate
      } else if (this.disableFuture) {
        return this.$Formatter.formatDate(new Date())
      }

      return undefined
    },
    isReadOnly() {
      return this.readonly || this.$store.getters.isLoading
    },
    inputClass() {
      let classObj = {
        'pointer-none': this.$store.getters.isLoading || this.disabled || this.readonly,
      }
      if (this.$validate.DataValid(this.customInputClass)) {
        const arr = this.customInputClass.split(' ')
        arr.forEach(name => {
          if (this.$validate.DataValid(name.trim())) {
            classObj[name.trim()] = true
          }
        })
      }

      return classObj
    },
    locale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en'
        case 'cn':
          return 'zh-CN'
        default:
          return 'zh-HK'
      }
    },
    isManual() {
      return this.manualInput && !this.isYearMonthOnly && !this.isDateRange;
    }
  },
  data: () => ({
    icon: {
      mdiCalendarMinus,
      mdiClose,
      mdiCheck,
    },
    datePickerOpen: false,
    date: null,
  }),
  watch: {
    fieldValue: {
      handler(val) {
        if (!this.isManual || this.$validate.regexDateStr(val)) {
          this.date = val;
        } else {
          this.date = '';
        }
      },
      immediate: true,
    },
  },
  methods: {
    _updateFieldValue(val) {
      this.$emit('date-clicked', val)
      if (this.disabledActionButtons) {
        this._updateFieldValueFromAction()
      }
    },
    _updateFieldValueFromAction(clear = false) {
      if (clear === true) {
        this.date = this.isDateRange ? [] : ''
      }

      let _date = this.date
      if (this.isDateRange) {
        if (this.date.length === 2 && new Date(this.date[0]).getTime() > new Date(this.date[1]).getTime()) {
          _date = [this.date[1], this.date[0]]
        }
      }

      this.$refs.datePickerMenu.save(_date)
      this.$emit('update:fieldValue', _date)
      this.$emit('changed', _date)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
      this.datePickerOpen = false
    },
    _updateFieldInputValue(val) {
      let value = this.$Formatter.numbersOnlyString(val);
      value =  this.$Formatter.maskFormat(value, [4, 2, 2], '-');
      if (this.$validate.regexDateStr(value)) {
        this.date = value;
      } else {
        this.date = '';
      }

      this.$emit('update:fieldValue', value)
      this.$emit('changed', value)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
      this.datePickerOpen = false
    },
    dateFormat() {
      if (!this.$validate.DataValid(this.date)) {
        return ''
      }

      if (this.isDateRange) {
        if (Array.isArray(this.date)) {
          let _date = this.date
          if (this.date.length === 2 && new Date(this.date[0]).getTime() > new Date(this.date[1]).getTime()) {
            _date = [this.date[1], this.date[0]]
          }
          return `${_date[0]} (${this.$d(new Date(_date[0]), 'weekdayShort')})${_date.length > 1 ? ` - ${_date[1]} (${this.$d(new Date(_date[1]), 'weekdayShort')})` : ''}`
        }
      } else if (this.isYearMonthOnly) {
        return this.$d(new Date(this.date), 'yearMonth')
      }

      return `${this.date} (${this.$d(new Date(this.date), 'weekdayShort')})`
    },
    formatInput(event) {
      if (this.fieldValue && this.fieldValue.length >= 10) {
        event.preventDefault();
        return;
      }
      return this.$Formatter.date(event)
    }
  },
}
</script>
